/**
 * Indicadores de situação de RH do cadastro de Pessoas, sobre o recorte ATUAL.
 *
 * Samuel, 17/09/2026: "esses indicadores tem que mexer conforme o filtro usado".
 * Por isso a faixa lê `resumo-filtrado`, que roda a MESMA consulta da grade, e não
 * `resumo`, que fala do cadastro inteiro e nunca se mexe.
 *
 * `page` e `per_page` ficam de fora da chave e da consulta de propósito: a pergunta
 * é quantas pessoas existem no recorte, não quantas couberam na página, e sem isso
 * virar a página refaria a busca sem mudar um número sequer.
 *
 * Rótulo em cima, valor embaixo, no mesmo desenho das outras faixas. Sem parêntese
 * e sem travessão encaixando informação.
 */
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { http } from '../../core/api/client'
import type { ListParams } from '../../core/api/resource'
import { fmtNum } from '../format'
import { useI18n } from '@/core/i18n/useI18n'
import { useDeclarativeText } from '../i18n/declarativeText'

export interface ResumoSituacao {
  total: number
  por_situacao: { situacao: string; label: string; total: number }[]
}

/** Só o que recorta linhas. Paginação e ordenação não mudam quantas existem. */
const FORA = new Set(['page', 'per_page', 'order_by', 'order_dir'])

export function ResumoSituacaoBanner({ path, name, params }: {
  /** Caminho da lista, ex.: '/personnel'. A faixa lê `${path}/resumo-filtrado`. */
  path: string
  /** Nome do recurso, primeiro elemento da queryKey: quem invalida a lista
   *  invalida a faixa junto. */
  name: string
  params?: ListParams
}) {
  const { t } = useI18n()
  const tt = useDeclarativeText()
  const recorte: ListParams = Object.fromEntries(
    Object.entries(params ?? {}).filter(([k, v]) => !FORA.has(k) && v !== undefined && v !== ''),
  )
  const q = useQuery({
    queryKey: [name, 'resumo-filtrado', recorte],
    queryFn: ({ signal }) => http.get<ResumoSituacao>(`${path}/resumo-filtrado`, { params: recorte, signal }),
  })
  const r = q.data
  if (!r) return null
  const itens = [
    { rotulo: tt('Total'), valor: fmtNum(r.total) },
    ...r.por_situacao.map((s) => ({ rotulo: tt(s.label), valor: fmtNum(s.total) })),
  ]
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      {itens.map((i) => (
        <Box key={i.rotulo}>
          <Typography variant="caption" color="text.secondary" display="block">{i.rotulo}</Typography>
          <Typography variant="body2" fontWeight={600}>{i.valor}</Typography>
        </Box>
      ))}
      {q.isError ? (
        <Typography variant="caption" color="text.secondary">{t('common.error')}</Typography>
      ) : null}
    </Box>
  )
}
